/**
 * Loads the sessions a student actually attended and the one closest to now.
 * Ready once the student, their makeup destinations, and those session docs
 * are in hand (or the wait for a missing doc runs out). `whenReady` resolves
 * only after that, so a caller does not treat a still-loading lookup as
 * "never attended."
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { classSessionAtoms, makeupAtoms, studentAtoms } from "#atoms"
import { NULL_ID } from "#classes/Firebase"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useFilteredDocs,
  useOneDoc,
  useSomeDocs,
} from "#hooks/jotai/useSubscription"

import {
  attendedSessionIds,
  makeupAttendedMissedSessionIds,
  nearestAttendedSessionId,
} from "./nearestAttendedSession"

const EMPTY_IDS: Uuid[] = []
const MAKEUP_WAIT_MS = 3000
const SESSION_WAIT_MS = 5000

export type AttendedSessionStamp = {
  ready: boolean
  sessionId: string | undefined
}

export const useNearestAttendedSession = (studentId: Uuid | undefined) => {
  const student = useOneDoc(studentAtoms, DOC_TYPES.STUDENTS, studentId)
  const makeupQuery = useMemo(
    () => ({
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )
  const makeups = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    makeupQuery
  )

  const missedKey = useMemo(
    () => makeupAttendedMissedSessionIds(student?.attendance).sort().join(","),
    [student?.attendance]
  )
  const makeupsCoverMissed = useMemo(() => {
    const missed = missedKey ? missedKey.split(",") : []
    return missed.every((id) =>
      makeups.some((makeup) => makeup.missedSessionId === id)
    )
  }, [missedKey, makeups])

  const [settledStudentId, setSettledStudentId] = useState<string | null>(null)
  const makeupSettled = !!student && settledStudentId === student.id

  useEffect(() => {
    if (!student) return
    if (makeupsCoverMissed) {
      setSettledStudentId(student.id)
      return
    }
    const timer = setTimeout(
      () => setSettledStudentId(student.id),
      MAKEUP_WAIT_MS
    )
    return () => clearTimeout(timer)
  }, [student, makeupsCoverMissed])

  const sessionIds = useMemo(() => {
    if (!student || !makeupSettled) return EMPTY_IDS
    return attendedSessionIds(student.attendance, makeups).sort()
  }, [student, makeupSettled, makeups])

  const sessions = useSomeDocs(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionIds
  )
  const loadedCount = sessionIds.filter((id) =>
    sessions.some((session) => session.id === id)
  ).length
  const stampKey = `${student?.id ?? ""}:${sessionIds.join(",")}`

  const [sessionsSettledKey, setSessionsSettledKey] = useState<string | null>(
    null
  )
  const sessionsSettled = makeupSettled && sessionsSettledKey === stampKey

  useEffect(() => {
    if (!makeupSettled || !student) return
    if (sessionIds.length === 0 || loadedCount === sessionIds.length) {
      setSessionsSettledKey(stampKey)
      return
    }
    const timer = setTimeout(
      () => setSessionsSettledKey(stampKey),
      SESSION_WAIT_MS
    )
    return () => clearTimeout(timer)
  }, [makeupSettled, student, sessionIds.length, loadedCount, stampKey])

  const ready = !!student && makeupSettled && sessionsSettled
  const attendedIds = useMemo(() => new Set(sessionIds), [sessionIds])
  const sessionId = ready
    ? nearestAttendedSessionId(sessions, attendedIds, new Date().toISOString())
    : undefined

  const stampRef = useRef<AttendedSessionStamp>({
    ready: false,
    sessionId: undefined,
  })
  stampRef.current = { ready, sessionId }
  const waitersRef = useRef<Array<() => void>>([])

  useEffect(() => {
    if (!ready) return
    const waiters = waitersRef.current
    waitersRef.current = []
    waiters.forEach((resolve) => resolve())
  }, [ready, sessionId])

  const whenReady = useCallback(() => {
    if (stampRef.current.ready) return Promise.resolve(stampRef.current)
    return new Promise<AttendedSessionStamp>((resolve) => {
      waitersRef.current.push(() => resolve(stampRef.current))
    })
  }, [])

  return { ready, sessionId, whenReady }
}
